
// TilesBridgesAnimation.tsx
import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Sparkles, Brain, Link } from 'lucide-react';
import './TilesBridgesAnimation.css';

const TilesBridgesAnimation: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const animate = () => {
      if (containerRef.current) {
        containerRef.current.classList.remove('animate');
        void containerRef.current.offsetWidth; // Force reflow
        containerRef.current.classList.add('animate');
      }
    };

    // Intersection observer to detect when component is visible
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animate();
        }
      });
    }, { threshold: 0.3 });

    if (containerRef.current) {
      observer.observe(containerRef.current);
      animate(); // Initial animation
    }

    // Auto-restart animation
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        animate();
      }
    }, 4500); // 4-second animation + 0.5s pause

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
      clearInterval(intervalId);
    };
  }, []);

  return (
      <div ref={containerRef} className="tiles-bridges-animation animate">
        {/* Background particles */}
        <div className="animation-particles">
          {[...Array(15)].map((_, i) => (
              <div
                  key={i}
                  className="animation-particle"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 5}s`,
                    width: `${Math.random() * 4 + 2}px`,
                    height: `${Math.random() * 4 + 2}px`,
                    opacity: Math.random() * 0.5 + 0.1
                  }}
              />
          ))}
        </div>

        <div className="animation-container">
          {/* Glass Surface */}
          <div className="animation-glass-surface">
            <div className="glass-primary-layer"></div>
            <div className="glass-accent-layer"></div>
            <div className="glass-shimmer-effect"></div>
          </div>

          {/* Main tiles */}
          <motion.div className="animation-tile document-tile">
            <div className="tile-inner">
              <div className="tile-icon">
                <FileText size={20} />
                <div className="icon-glow"></div>
              </div>
              <div className="tile-label">Document</div>
            </div>
          </motion.div>

          <motion.div className="animation-tile concept-tile">
            <div className="tile-inner">
              <div className="tile-icon">
                <Brain size={20} />
                <div className="icon-glow"></div>
              </div>
              <div className="tile-label">Concept</div>
            </div>
          </motion.div>

          {/* Connection bridge */}
          <div className="animation-bridge">
            <div className="bridge-line"></div>
            <div className="bridge-pulse"></div>
            <div className="bridge-particles">
              {[...Array(5)].map((_, i) => (
                  <div key={i} className="bridge-particle" />
              ))}
            </div>
          </div>

          {/* Link indicator */}
          <div className="connection-indicator">
            <Link size={16} />
          </div>

          {/* Insight flash */}
          <div className="insight-flash"></div>

          {/* Insight popup */}
          <motion.div className="animation-insight">
            <div className="insight-inner">
              <div className="insight-icon">
                <Sparkles size={18} />
              </div>
              <div className="insight-content">
                <div className="insight-title">Bridged Insight</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
  );
};

export default TilesBridgesAnimation;